import { useState, useCallback } from 'react';
import { supabase } from '../supabase';
import { useAuth } from '../auth';

export interface ProrationPreview {
  amountDue: number;
  credit: number;
  prorationDate: Date;
  currency: string;
}

export interface QuantityProrationPreview {
  amountDue: number;
  currentQuantity: number;
  newQuantity: number;
  seatsDifference: number;
  isIncrease: boolean;
  pricePerSeat: number;
  prorationDate: Date;
  currentPeriodEnd: Date;
  daysRemaining: number;
  totalDays: number;
  currency: string;
  billingPeriod: 'month' | 'year';
}

export interface QuantityUpdateResult {
  success: boolean;
  newQuantity: number;
  previousQuantity: number;
  activeUsers: number;
}

export interface UseStripeCheckoutReturn {
  createCheckout: (planId: string, billingPeriod: 'monthly' | 'yearly', quantity?: number, addonIds?: string[], referralCode?: string) => Promise<void>;
  openPortal: () => Promise<void>;
  previewProration: (newPlanId: string, billingPeriod: 'monthly' | 'yearly') => Promise<ProrationPreview | null>;
  previewProrationBatch: (newPlanIds: string[], billingPeriod: 'monthly' | 'yearly') => Promise<Record<string, any> | null>;
  previewQuantityChange: (newQuantity: number) => Promise<QuantityProrationPreview | null>;
  changeSubscription: (newPlanId: string, billingPeriod: 'monthly' | 'yearly') => Promise<boolean>;
  updateQuantity: (newQuantity: number) => Promise<QuantityUpdateResult | null>;
  addAddon: (addonId: string) => Promise<boolean>;
  removeAddon: (addonId: string) => Promise<boolean>;
  loading: boolean;
  error: string | null;
}

/**
 * Hook per gestire le operazioni Stripe
 * - Checkout per nuovi abbonamenti
 * - Customer Portal per gestione abbonamento
 * - Preview e cambio piano con proration
 */
export function useStripeCheckout(): UseStripeCheckoutReturn {
  const { userProfile } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Crea una sessione di checkout Stripe e reindirizza l'utente
   * @param quantity - Numero di utenti (opzionale, se non fornito usa il count attuale)
   */
  const createCheckout = useCallback(async (
    planId: string,
    billingPeriod: 'monthly' | 'yearly',
    quantity?: number,
    addonIds?: string[],
    referralCode?: string
  ): Promise<void> => {
    if (!userProfile?.organization_id) {
      setError('Organizzazione non trovata');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Assicuriamoci che quantity sia sempre passata se >= 1
      const checkoutBody: Record<string, any> = {
        organizationId: userProfile.organization_id,
        planId,
        billingPeriod,
      };

      if (typeof quantity === 'number' && quantity >= 1) {
        checkoutBody.quantity = quantity;
      }

      if (addonIds && addonIds.length > 0) {
        checkoutBody.addonIds = addonIds;
      }

      if (referralCode) {
        checkoutBody.referralCode = referralCode;
      }

      console.log('[Checkout] Sending to stripe-create-checkout:', checkoutBody);

      const { data, error: invokeError } = await supabase.functions.invoke('stripe-create-checkout', {
        body: checkoutBody,
      });

      if (invokeError) {
        throw new Error(invokeError.message);
      }

      if (!data?.checkoutUrl) {
        throw new Error('URL di checkout non ricevuto');
      }

      // Reindirizza a Stripe Checkout
      window.location.href = data.checkoutUrl;

    } catch (err) {
      console.error('Error creating checkout:', err);
      setError(err instanceof Error ? err.message : 'Errore durante la creazione del checkout');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [userProfile?.organization_id]);

  /**
   * Apre il Customer Portal Stripe
   */
  const openPortal = useCallback(async (): Promise<void> => {
    if (!userProfile?.organization_id) {
      setError('Organizzazione non trovata');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { data, error: invokeError } = await supabase.functions.invoke('stripe-create-portal', {
        body: {
          organizationId: userProfile.organization_id,
        },
      });

      if (invokeError) {
        throw new Error(invokeError.message);
      }

      if (!data?.portalUrl) {
        throw new Error('URL del portale non ricevuto');
      }

      // Reindirizza al Customer Portal
      window.location.href = data.portalUrl;

    } catch (err) {
      console.error('Error opening portal:', err);
      setError(err instanceof Error ? err.message : 'Errore durante l\'apertura del portale');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [userProfile?.organization_id]);

  /**
   * Calcola la preview della proration per un cambio piano
   * Ritorna la risposta completa della Edge Function per permettere al componente
   * di gestire sia success, noChange, che preview
   */
  const previewProration = useCallback(async (
    newPlanId: string,
    billingPeriod: 'monthly' | 'yearly'
  ): Promise<any> => {
    if (!userProfile?.organization_id) {
      setError('Organizzazione non trovata');
      return null;
    }

    try {
      const { data, error: invokeError } = await supabase.functions.invoke('stripe-preview-proration', {
        body: {
          organizationId: userProfile.organization_id,
          newPlanId,
          newBillingPeriod: billingPeriod,
        },
      });

      if (invokeError) {
        throw new Error(invokeError.message);
      }

      // Ritorna la risposta completa (success, noChange, preview, etc.)
      return data;

    } catch (err) {
      console.error('Error previewing proration:', err);
      throw err;  // Rilancia per permettere al componente di gestire l'errore
    }
  }, [userProfile?.organization_id]);

  /**
   * Calcola la preview della proration per più piani in una singola chiamata batch
   */
  const previewProrationBatch = useCallback(async (
    newPlanIds: string[],
    billingPeriod: 'monthly' | 'yearly'
  ): Promise<Record<string, any> | null> => {
    if (!userProfile?.organization_id) {
      setError('Organizzazione non trovata');
      return null;
    }

    try {
      const { data, error: invokeError } = await supabase.functions.invoke('stripe-preview-proration', {
        body: {
          organizationId: userProfile.organization_id,
          newPlanIds,
          newBillingPeriod: billingPeriod,
        },
      });

      if (invokeError) {
        throw new Error(invokeError.message);
      }

      return data?.previews ?? null;

    } catch (err) {
      console.error('Error previewing batch proration:', err);
      throw err;
    }
  }, [userProfile?.organization_id]);

  /**
   * Esegue il cambio di piano con proration
   */
  const changeSubscription = useCallback(async (
    newPlanId: string,
    billingPeriod: 'monthly' | 'yearly'
  ): Promise<boolean> => {
    if (!userProfile?.organization_id) {
      setError('Organizzazione non trovata');
      return false;
    }

    setLoading(true);
    setError(null);

    try {
      const { data, error: invokeError } = await supabase.functions.invoke('stripe-change-subscription', {
        body: {
          organizationId: userProfile.organization_id,
          newPlanId,
          newBillingPeriod: billingPeriod,
        },
      });

      if (invokeError) {
        throw new Error(invokeError.message);
      }

      if (data?.success) {
        // Sync: aggiunge i permessi del nuovo piano (upsert, non cancella quelli custom)
        try {
          await supabase.functions.invoke('manage-organization-roles', {
            body: {
              action: 'sync_permissions',
              organizationId: userProfile.organization_id,
              planId: newPlanId,
            },
          });
          const { data: { user: currentUser } } = await supabase.auth.getUser();
          if (currentUser) {
            const { usePermissionsStore } = await import('../stores/permissionsStore');
            const isSuperAdmin = currentUser.email?.endsWith('@obly.it') || currentUser.email?.endsWith('@flusia.it') || false;
            await usePermissionsStore.getState().fetchPermissions(currentUser.id, isSuperAdmin);
          }
        } catch (syncErr) {
          console.error('Error syncing permissions after plan change:', syncErr);
        }
      }

      return data?.success || false;

    } catch (err) {
      console.error('Error changing subscription:', err);
      setError(err instanceof Error ? err.message : 'Errore durante il cambio di piano');
      return false;
    } finally {
      setLoading(false);
    }
  }, [userProfile?.organization_id]);

  /**
   * Calcola la preview della proration per un cambio di quantity (seats)
   * @param newQuantity - Nuova quantity desiderata
   */
  const previewQuantityChange = useCallback(async (
    newQuantity: number
  ): Promise<QuantityProrationPreview | null> => {
    if (!userProfile?.organization_id) {
      setError('Organizzazione non trovata');
      return null;
    }

    setLoading(true);
    setError(null);

    try {
      const { data, error: invokeError } = await supabase.functions.invoke('stripe-preview-quantity', {
        body: {
          organizationId: userProfile.organization_id,
          newQuantity,
        },
      });

      if (invokeError) {
        throw new Error(invokeError.message);
      }

      if (!data?.success || data?.noChange) {
        return null;
      }

      const preview = data.preview;
      return {
        amountDue: preview.amountDue,
        currentQuantity: preview.currentQuantity,
        newQuantity: preview.newQuantity,
        seatsDifference: preview.seatsDifference,
        isIncrease: preview.isIncrease,
        pricePerSeat: preview.pricePerSeat,
        prorationDate: new Date(preview.prorationDate),
        currentPeriodEnd: new Date(preview.currentPeriodEnd),
        daysRemaining: preview.daysRemaining,
        totalDays: preview.totalDays,
        currency: preview.currency || 'EUR',
        billingPeriod: preview.billingPeriod,
      };

    } catch (err) {
      console.error('Error previewing quantity change:', err);
      setError(err instanceof Error ? err.message : 'Errore durante il calcolo della proration');
      return null;
    } finally {
      setLoading(false);
    }
  }, [userProfile?.organization_id]);

  /**
   * Aggiorna manualmente il numero di seats (posti) nell'abbonamento
   * @param newQuantity - Nuovo numero di seats (deve essere >= utenti attivi)
   */
  const updateQuantity = useCallback(async (
    newQuantity: number
  ): Promise<QuantityUpdateResult | null> => {
    if (!userProfile?.organization_id) {
      setError('Organizzazione non trovata');
      return null;
    }

    setLoading(true);
    setError(null);

    try {
      const { data, error: invokeError } = await supabase.functions.invoke('stripe-update-quantity-manual', {
        body: {
          organizationId: userProfile.organization_id,
          quantity: newQuantity,
        },
      });

      if (invokeError) {
        throw new Error(invokeError.message);
      }

      if (!data?.success) {
        throw new Error(data?.message || 'Errore durante l\'aggiornamento dei seats');
      }

      return {
        success: true,
        newQuantity: data.newQuantity,
        previousQuantity: data.previousQuantity,
        activeUsers: data.activeUsers,
      };

    } catch (err) {
      console.error('Error updating quantity:', err);
      setError(err instanceof Error ? err.message : 'Errore durante l\'aggiornamento dei seats');
      return null;
    } finally {
      setLoading(false);
    }
  }, [userProfile?.organization_id]);

  // ── Add-on management ──

  const addAddon = useCallback(async (addonId: string): Promise<boolean> => {
    if (!userProfile?.organization_id) return false;

    setLoading(true);
    setError(null);
    try {
      const { data, error: fnError } = await supabase.functions.invoke('stripe-manage-addons', {
        body: { action: 'add_addon', organizationId: userProfile.organization_id, addonId },
      });
      if (fnError) throw fnError;
      if (data?.error) throw new Error(data.error);
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore nell\'aggiunta dell\'add-on');
      return false;
    } finally {
      setLoading(false);
    }
  }, [userProfile?.organization_id]);

  const removeAddon = useCallback(async (addonId: string): Promise<boolean> => {
    if (!userProfile?.organization_id) return false;

    setLoading(true);
    setError(null);
    try {
      const { data, error: fnError } = await supabase.functions.invoke('stripe-manage-addons', {
        body: { action: 'remove_addon', organizationId: userProfile.organization_id, addonId },
      });
      if (fnError) throw fnError;
      if (data?.error) throw new Error(data.error);
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore nella rimozione dell\'add-on');
      return false;
    } finally {
      setLoading(false);
    }
  }, [userProfile?.organization_id]);

  return {
    createCheckout,
    openPortal,
    previewProration,
    previewProrationBatch,
    previewQuantityChange,
    changeSubscription,
    updateQuantity,
    addAddon,
    removeAddon,
    loading,
    error,
  };
}
